import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { QrResolutionError, resolveQrOnce } from '../lib/session.js';
import { Icon } from '../components/Icon.js';
import { Logo } from '../components/Logo.js';
import { labels } from '../labels.js';
import styles from './ResolveQrPage.module.css';

export function ResolveQrPage() {
  const { qrToken } = useParams<{ qrToken: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!qrToken) return;
    let cancelled = false;

    // resolveQrOnce, et non un appel direct : le StrictMode de React execute
    // cet effet deux fois en developpement, et deux resolutions parties avant
    // que la premiere n'ait range son jeton comptaient pour deux convives.
    // Le jeton, le contexte d'affichage et la memorisation du QR sont ranges
    // par resolveQrOnce. Voir lib/session.ts.
    resolveQrOnce(qrToken)
      .then(() => {
        if (cancelled) return;
        navigate('/menu', { replace: true });
      })
      .catch((e) => {
        if (cancelled) return;
        const code = e instanceof QrResolutionError ? e.code : null;
        if (code === 'QR_TOKEN_INVALID') setError(labels.errors.qrInvalid);
        else if (code === 'TABLE_INACTIVE') setError(labels.errors.tableInactive);
        else if (code) setError(labels.errors.generic);
        else setError(labels.errors.network);
      });

    return () => { cancelled = true; };
  }, [qrToken, navigate]);

  return (
    <main className={`client-screen ${styles.screen}`} aria-busy={!error}>
      <div className={styles.body}>
        {error ? (
          <>
            <span className={styles.iconError}><Icon name="alert" size={30} /></span>
            <h1 className={styles.title}>{labels.resolve.errorTitle}</h1>
            <p className={styles.text} role="alert">{error}</p>
            <div className={styles.actions}>
              <button onClick={() => window.location.reload()} className="btn-secondary btn-lg btn-block">
                <Icon name="refresh" size={20} />
                {labels.errors.retry}
              </button>
              <Link to="/scan" className={styles.link}>{labels.resolve.scanAgain}</Link>
            </div>
          </>
        ) : (
          <>
            <span className={styles.loader} aria-hidden="true"><Logo size={56} /></span>
            <h1 className={styles.title}>{labels.resolve.title}</h1>
            <p className={styles.text}>{labels.resolve.text}</p>
            <span className={styles.progress} aria-hidden="true" />
          </>
        )}
      </div>
      <footer className={styles.footer}>
        <Logo size={24} withWordmark tone="dark" />
      </footer>
    </main>
  );
}
